import React from 'react';
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, LabelList, XAxis, YAxis,
} from 'recharts';
import {
  CalendarDays, Clock, CreditCard, Hourglass, TrendingUp, Users,
} from 'lucide-react';
import { Badge } from './ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Separator } from './ui/separator';
import {
  ChartContainer, ChartTooltip, ChartTooltipContent,
} from './ui/chart';
import { cn } from '../lib/utils';

/**
 * Las estadísticas del negocio.
 *
 * QUÉ CAMBIÓ Y POR QUÉ
 * Estaba escrito a mano adentro de `Dashboard.jsx`: seis tarjetas con emojis y
 * un "gráfico" de treinta `<div>` con la altura puesta en porcentaje. Eso
 * dibuja barras, pero no es un gráfico: no tiene ejes, no tiene escala legible,
 * el número sólo aparece en el `title` del navegador —o sea, pasando el mouse y
 * esperando— y una barra de dos píxeles no se distingue de una de cero.
 *
 * Ahora usa los componentes del sistema: `Card` para cada bloque y
 * `ChartContainer` (ui/chart.jsx, que envuelve Recharts) para los dos gráficos.
 *
 * OJO CON LOS GRÁFICOS ADENTRO DEL PANEL
 * Recharts posiciona todo con `<g transform="translate(...)">`, y en SVG eso es
 * una propiedad de presentación que mapea a la propiedad CSS `transform` — que
 * el panel apaga con `!important` para todo `.dashboard-container`. Sin la
 * excepción de `Dashboard.css` (buscar `[data-chart]`), un gráfico acá adentro
 * no se ve torcido: se ve amontonado en el origen. Está medido y anotado allá.
 *
 * DE DÓNDE SALEN LOS NÚMEROS
 * De ningún lado nuevo: los calcula `Dashboard.jsx` como siempre y llegan por
 * props ya resueltos. Este componente no consulta nada ni decide nada, sólo
 * dibuja — que es lo que lo hace fácil de mirar y de cambiar.
 */

/** El naranja de la marca. En el panel `aplicarBranding` lo reescribe en
 *  tiempo de ejecución, así que un gráfico dibujado con esto toma el color que
 *  el negocio eligió, sin que haya que pasarle nada. */
const MARCA = 'var(--brand-primary, var(--aturno-orange))';

const config = {
  ingresos: { label: 'Ingresos', color: MARCA },
  turnos: { label: 'Turnos', color: MARCA },
};

/**
 * Una métrica suelta. El número grande primero y la explicación abajo: al
 * revés obliga a leer la etiqueta para saber si el número te importa.
 */
function Metrica({ Icono, valor, etiqueta, detalle, className }) {
  return (
    <Card className={cn('overflow-hidden', className)}>
      <CardContent className="flex flex-col gap-2 p-4 sm:flex-row sm:items-start sm:gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-600">
          <Icono className="h-[1.05rem] w-[1.05rem]" />
        </span>
        <div className="min-w-0">
          {/* `truncate` y `title`: el servicio más popular es un nombre escrito
              por el negocio y puede ser largo ("Coloración completa con
              tratamiento"). Sin esto rompía la grilla. */}
          <p className="m-0 truncate text-xl font-bold leading-tight text-slate-900" title={String(valor)}>
            {valor}
          </p>
          <p className="m-0 mt-0.5 text-[0.78rem] leading-snug text-slate-500">{etiqueta}</p>
          {detalle && (
            <p className="m-0 mt-1 text-[0.72rem] leading-snug text-slate-400">{detalle}</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/** Una fila del resumen operativo. */
function Fila({ Icono, etiqueta, valor }) {
  return (
    <div className="flex items-center gap-3 py-2">
      <Icono className="h-4 w-4 shrink-0 text-slate-400" />
      <span className="min-w-0 flex-1 truncate text-[0.83rem] text-slate-600">{etiqueta}</span>
      <span className="shrink-0 text-[0.88rem] font-semibold text-slate-900">{valor}</span>
    </div>
  );
}

/** Lo que se muestra cuando todavía no hay con qué llenar un gráfico. */
function SinDatos({ children }) {
  return (
    <div className="flex h-full min-h-[180px] flex-col items-center justify-center gap-1 px-6 text-center">
      <p className="m-0 text-sm font-semibold text-slate-500">Todavía no hay datos</p>
      <p className="m-0 text-xs leading-relaxed text-slate-400">{children}</p>
    </div>
  );
}

const EstadisticasPanel = ({
  basicas, rendimiento, operativas, serieDeIngresos, distribucionDeServicios,
  formatearMoneda,
}) => {
  // Recharts quiere una fila por punto, con sus campos nombrados; las
  // calculadoras devuelven dos arrays paralelos. La conversión va acá y no en
  // Dashboard.jsx porque es una necesidad de cómo se dibuja, no del cálculo.
  const ingresos = serieDeIngresos.days.map((dia, i) => ({
    dia: String(dia),
    ingresos: serieDeIngresos.revenues[i],
  }));

  const totalDelPeriodo = serieDeIngresos.revenues.reduce((a, b) => a + b, 0);
  const promedioDiario = totalDelPeriodo / (serieDeIngresos.revenues.length || 1);
  const mejorDia = Math.max(0, ...serieDeIngresos.revenues);
  const diasConMovimiento = serieDeIngresos.revenues.filter((r) => r > 0).length;
  const huboIngresos = totalDelPeriodo > 0;

  // Sólo los seis servicios más usados: con veinte, las etiquetas del eje no
  // entran y el gráfico deja de decir nada.
  const servicios = [...distribucionDeServicios]
    .sort((a, b) => b.count - a.count)
    .slice(0, 6)
    .map((s) => ({ ...s, nombre: s.name }));

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h2 className="m-0 text-lg font-bold text-slate-900">Estadísticas</h2>
        <p className="m-0 mt-1 max-w-[70ch] text-sm leading-snug text-slate-500">
          Cómo viene el mes, y qué pasó en los últimos 30 días.
        </p>
      </header>

      {/* Las seis métricas del mes. */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
        <Metrica
          Icono={TrendingUp}
          valor={formatearMoneda(basicas.monthlyRevenue)}
          etiqueta="Ingresos del mes"
          detalle={`${basicas.confirmedAppointments} turnos confirmados`}
        />
        <Metrica
          Icono={CalendarDays}
          valor={basicas.totalAppointments}
          etiqueta="Turnos del mes"
          detalle={`${basicas.confirmationRate}% se confirmaron`}
        />
        <Metrica
          Icono={Users}
          valor={operativas.uniqueClients}
          etiqueta="Clientes únicos"
        />
        <Metrica
          Icono={CreditCard}
          valor={formatearMoneda(operativas.averageTicket)}
          etiqueta="Ticket promedio"
        />
        <Metrica
          Icono={Clock}
          valor={`${operativas.occupancyRate}%`}
          etiqueta="Ocupación"
          detalle={`Turnos de ${operativas.averageDuration} min en promedio`}
        />
        <Metrica
          Icono={Hourglass}
          valor={operativas.pendingAppointments}
          etiqueta="Turnos pendientes"
          detalle={`${operativas.upcomingAppointments} en los próximos 7 días`}
        />
      </div>

      <div className="grid gap-4 xl:grid-cols-[1.6fr_1fr]">
        {/* Ingresos día por día. */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <CardTitle className="text-base">Ingresos, últimos 30 días</CardTitle>
                <CardDescription>Sólo turnos confirmados</CardDescription>
              </div>
              {huboIngresos && (
                <Badge variant="secondary" className="shrink-0 font-semibold">
                  {formatearMoneda(totalDelPeriodo)} en total
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {huboIngresos ? (
              <>
                <ChartContainer config={config} className="aspect-auto h-[240px] w-full">
                  <AreaChart data={ingresos} margin={{ left: 4, right: 8, top: 8 }}>
                    <defs>
                      <linearGradient id="relleno-ingresos" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="var(--color-ingresos)" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="var(--color-ingresos)" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid vertical={false} strokeDasharray="3 3" />
                    <XAxis
                      dataKey="dia"
                      tickLine={false}
                      axisLine={false}
                      tickMargin={8}
                      // Una etiqueta cada cinco días: treinta números pegados no
                      // se leen y encima se pisan entre ellos.
                      interval={4}
                    />
                    <YAxis
                      tickLine={false}
                      axisLine={false}
                      width={64}
                      tickFormatter={(v) => formatearMoneda(v)}
                    />
                    <ChartTooltip
                      content={
                        <ChartTooltipContent
                          labelFormatter={(d) => `Día ${d}`}
                          formatter={(v) => formatearMoneda(v)}
                        />
                      }
                    />
                    <Area
                      dataKey="ingresos"
                      type="monotone"
                      stroke="var(--color-ingresos)"
                      strokeWidth={2}
                      fill="url(#relleno-ingresos)"
                    />
                  </AreaChart>
                </ChartContainer>

                <Separator className="my-3" />

                <div className="grid grid-cols-3 gap-2 text-center">
                  <div>
                    <p className="m-0 text-[0.95rem] font-bold text-slate-900">
                      {formatearMoneda(promedioDiario)}
                    </p>
                    <p className="m-0 text-[0.72rem] text-slate-500">Promedio por día</p>
                  </div>
                  <div>
                    <p className="m-0 text-[0.95rem] font-bold text-slate-900">
                      {formatearMoneda(mejorDia)}
                    </p>
                    <p className="m-0 text-[0.72rem] text-slate-500">Mejor día</p>
                  </div>
                  <div>
                    <p className="m-0 text-[0.95rem] font-bold text-slate-900">
                      {diasConMovimiento} de 30
                    </p>
                    <p className="m-0 text-[0.72rem] text-slate-500">Días con turnos</p>
                  </div>
                </div>
              </>
            ) : (
              <SinDatos>
                Cuando tengas turnos confirmados, acá vas a ver cuánto entró
                cada día.
              </SinDatos>
            )}
          </CardContent>
        </Card>

        {/* Resumen operativo. */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Resumen</CardTitle>
            <CardDescription>Cómo está funcionando</CardDescription>
          </CardHeader>
          <CardContent className="pt-1">
            <Fila
              Icono={TrendingUp}
              etiqueta="Servicio más pedido"
              valor={basicas.mostPopularService}
            />
            <Separator />
            <Fila Icono={Users} etiqueta="Quien más atendió" valor={rendimiento.mostActiveStaff} />
            <Separator />
            <Fila Icono={Clock} etiqueta="Horario pico" valor={rendimiento.peakHour} />
            <Separator />
            <Fila
              Icono={CalendarDays}
              etiqueta="Recurso más usado"
              valor={rendimiento.mostUsedResource}
            />
            <Separator />
            <Fila
              Icono={Hourglass}
              etiqueta="Turnos cancelados"
              valor={operativas.cancelledAppointments}
            />
          </CardContent>
        </Card>
      </div>

      {/* Qué se pide más. Barras horizontales y no una torta: los nombres de
          los servicios son largos, y en una torta hay que ir y volver a la
          referencia para saber cuál es cuál. */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Qué te piden más</CardTitle>
          <CardDescription>Turnos confirmados este mes</CardDescription>
        </CardHeader>
        <CardContent>
          {servicios.length > 0 ? (
            <ChartContainer
              config={config}
              className="aspect-auto w-full"
              style={{ height: Math.max(140, servicios.length * 46) }}
            >
              <BarChart
                data={servicios}
                layout="vertical"
                margin={{ left: 8, right: 56, top: 4, bottom: 4 }}
              >
                <YAxis
                  dataKey="nombre"
                  type="category"
                  tickLine={false}
                  axisLine={false}
                  width={150}
                  tickMargin={6}
                />
                <XAxis dataKey="count" type="number" hide />
                <ChartTooltip
                  content={
                    <ChartTooltipContent
                      formatter={(v, n, item) => (
                        `${v} ${v === 1 ? 'turno' : 'turnos'} · ${formatearMoneda(item?.payload?.revenue || 0)}`
                      )}
                    />
                  }
                />
                <Bar dataKey="count" radius={5}>
                  {servicios.map((s) => (
                    <Cell key={s.nombre} fill="var(--color-turnos)" />
                  ))}
                  {/* El número al final de la barra: sin esto hay que estimar
                      el largo contra un eje que está escondido. */}
                  <LabelList
                    dataKey="count"
                    position="right"
                    offset={8}
                    className="fill-slate-500"
                    fontSize={12}
                  />
                </Bar>
              </BarChart>
            </ChartContainer>
          ) : (
            <SinDatos>
              Todavía no hay turnos confirmados este mes para comparar servicios.
            </SinDatos>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EstadisticasPanel;
